import { useQuery } from "@tanstack/react-query";
import { Fragment, useMemo, useState } from "react";
import { format as formatSql } from "sql-formatter";

import { fetchCapabilityPerformance, fetchSlowCalls, fetchSlowRuns } from "@/lib/api";
import { useStore } from "@/lib/store";

import { formatDuration, formatInt } from "./formatters";

const control =
  "border-cyber-border bg-cyber-bg text-cyber-text rounded border px-2 py-1.5 text-xs";
const duration = (value: number | null) => (value === null ? "—" : formatDuration(value));

function ExecutionInput({ inputJson }: { inputJson: string | null }) {
  const content = useMemo(() => {
    if (inputJson === null) return { input: "Input not recorded" };
    let parsed: unknown;
    try {
      parsed = JSON.parse(inputJson);
    } catch {
      return { input: inputJson };
    }
    if (parsed && typeof parsed === "object" && "sql" in parsed && typeof parsed.sql === "string") {
      const { sql, ...otherFields } = parsed;
      let formatted = sql;
      try {
        formatted = formatSql(sql, { language: "clickhouse", tabWidth: 2 });
      } catch {
        // Unsupported syntax or truncated telemetry should still be inspectable.
      }
      return {
        sql: formatted,
        input: Object.keys(otherFields).length ? JSON.stringify(otherFields, null, 2) : null,
      };
    }
    return { input: JSON.stringify(parsed, null, 2) };
  }, [inputJson]);
  return (
    <div className="mt-2 min-w-0 space-y-2">
      {content.sql !== undefined && (
        <>
          <p>SQL</p>
          <pre className="bg-cyber-bg max-h-96 overflow-auto rounded p-3 text-left leading-relaxed break-words whitespace-pre-wrap">
            <code>{content.sql}</code>
          </pre>
        </>
      )}
      {content.input != null && (
        <>
          {content.sql !== undefined && <p>Other input fields</p>}
          <pre className="bg-cyber-bg max-h-80 overflow-auto rounded p-3 text-left break-words whitespace-pre-wrap">
            {content.input}
          </pre>
        </>
      )}
    </div>
  );
}

function SlowCalls({
  days,
  mine,
  capabilityId,
  profile,
}: {
  days: number;
  mine: boolean;
  capabilityId: string;
  profile: string;
}) {
  const selectSession = useStore((state) => state.selectSession);
  const { data, isPending, error } = useQuery({
    queryKey: ["performance", "calls", days, mine, capabilityId, profile],
    queryFn: () => fetchSlowCalls(days, mine, capabilityId, profile),
  });
  return (
    <section className="border-cyber-border bg-cyber-surface/20 space-y-3 rounded-lg border p-3">
      <h3 className="text-cyber-text text-sm">
        Slowest executions · {capabilityId} · {profile}
      </h3>
      {isPending && <p>Loading executions…</p>}
      {error && <p role="alert">Could not load executions: {error.message}</p>}
      {data?.rows.length === 0 && <p>No timed executions in this window.</p>}
      {data?.rows.map((row) => (
        <details key={row.executionId} className="border-cyber-border/50 rounded border p-2">
          <summary className="cursor-pointer">
            <span className="text-cyber-text">{duration(row.durationMs)}</span> · {row.status} ·{" "}
            {new Date(row.startedAt).toLocaleString()}
            {row.failureCode && ` · ${row.failureCode}`}
          </summary>
          <p className="mt-2 break-all">Execution: {row.executionId}</p>
          {row.jobId && <p className="break-all">Job: {row.jobId}</p>}
          {row.threadKey && (
            <button
              className="text-cyber-cyan mt-2 cursor-pointer hover:underline"
              onClick={() => selectSession(row.threadKey!)}
            >
              Open conversation
            </button>
          )}
          <ExecutionInput inputJson={row.inputJson} />
        </details>
      ))}
      {data?.hasMore && (
        <p>Showing the 50 slowest executions. Narrow the date range to inspect a smaller window.</p>
      )}
    </section>
  );
}

function Capabilities({ days, mine }: { days: number; mine: boolean }) {
  const [profile, setProfile] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<"totalMs" | "p95Ms" | "medianMs" | "slowCalls" | "calls">(
    "totalMs",
  );
  const [selected, setSelected] = useState<{ capabilityId: string; profile: string } | null>(null);
  const { data, isPending, error } = useQuery({
    queryKey: ["performance", "capabilities", days, mine, profile],
    queryFn: () => fetchCapabilityPerformance(days, mine, profile),
  });
  const rows = (data?.rows ?? [])
    .filter((row) => row.capabilityId.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => (b[sort] ?? -1) - (a[sort] ?? -1));
  return (
    <>
      <div className="flex flex-wrap gap-2">
        <input
          aria-label="Search capability performance"
          placeholder="Search capabilities"
          className={`${control} min-w-48 flex-1`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          aria-label="Execution profile"
          className={control}
          value={profile}
          onChange={(e) => {
            setProfile(e.target.value);
            setSelected(null);
          }}
        >
          <option value="">Both profiles</option>
          <option value="hosted">Hosted</option>
          <option value="remote">Remote</option>
        </select>
        <select
          aria-label="Sort capabilities"
          className={control}
          value={sort}
          onChange={(e) => setSort(e.target.value as typeof sort)}
        >
          <option value="totalMs">Total time</option>
          <option value="p95Ms">p95 duration</option>
          <option value="medianMs">Median duration</option>
          <option value="slowCalls">Calls &gt; 5s</option>
          <option value="calls">Call count</option>
        </select>
      </div>
      <p>
        All outcomes included. Durations exclude admission rejections, unfinished calls, and missing
        timings. Total time sums calls, including parallel work; it is not user waiting time.
      </p>
      {isPending && <p>Loading capability performance…</p>}
      {error && <p role="alert">Could not load performance: {error.message}</p>}
      {data && (
        <div className="overflow-x-auto">
          <table className="w-full text-right text-xs">
            <thead className="border-cyber-border border-b">
              <tr>
                {[
                  "Capability / profile",
                  "Calls",
                  "Timed",
                  "Median",
                  "p95",
                  "Total",
                  "> 5s",
                  "Failed",
                  "Timeouts",
                  "Denied",
                  "Unfinished",
                ].map((label) => (
                  <th key={label} className="p-2 font-medium whitespace-nowrap first:text-left">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const expanded =
                  selected?.capabilityId === row.capabilityId && selected.profile === row.profile;
                const toggle = () => setSelected(expanded ? null : row);
                return (
                  <Fragment key={`${row.capabilityId}:${row.profile}`}>
                    <tr
                      onClick={data.canInspect ? toggle : undefined}
                      className={`border-cyber-border/30 border-b ${data.canInspect ? "hover:bg-cyber-surface/30 cursor-pointer" : ""}`}
                    >
                      <td className="p-2 text-left">
                        <button
                          disabled={!data.canInspect}
                          aria-expanded={expanded}
                          onClick={(event) => {
                            event.stopPropagation();
                            toggle();
                          }}
                          className="text-cyber-cyan disabled:text-cyber-text text-left enabled:cursor-pointer enabled:hover:underline"
                        >
                          <span className="font-mono">{row.capabilityId}</span>
                          <span className="text-cyber-muted block">{row.profile}</span>
                          <span className="text-cyber-muted block text-[10px]">
                            {!data.canInspect
                              ? "Inspect in Mine · admin required for All"
                              : expanded
                                ? "Hide executions ▴"
                                : "Show slow executions ▾"}
                          </span>
                        </button>
                      </td>
                      {[
                        formatInt(row.calls),
                        formatInt(row.timedCalls),
                        duration(row.medianMs),
                        duration(row.p95Ms),
                        duration(row.totalMs),
                        formatInt(row.slowCalls),
                        formatInt(row.failed),
                        formatInt(row.timeouts),
                        formatInt(row.denied),
                        formatInt(row.running),
                      ].map((value, i) => (
                        <td key={i} className="p-2 whitespace-nowrap">
                          {value}
                        </td>
                      ))}
                    </tr>
                    {expanded && data.canInspect && (
                      <tr>
                        <td colSpan={11} className="p-2 text-left">
                          <SlowCalls
                            days={days}
                            mine={mine}
                            capabilityId={row.capabilityId}
                            profile={row.profile}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
          {rows.length === 0 && (
            <p className="py-6 text-center">No capabilities match these filters.</p>
          )}
        </div>
      )}
      <p>
        Timed is the percentile sample size. Small samples make p95 unstable. Timeouts are a subset
        of failures. Unfinished means no final outcome was recorded.
      </p>
      {data && !data.canInspect && (
        <p>
          Switch to Mine to inspect your executions. Inspecting other requesters’ inputs requires
          admin access.
        </p>
      )}
    </>
  );
}

function Runs({ days, mine }: { days: number; mine: boolean }) {
  const [kind, setKind] = useState("interactive");
  const selectSession = useStore((state) => state.selectSession);
  const { data, isPending, error } = useQuery({
    queryKey: ["performance", "runs", days, mine, kind],
    queryFn: () => fetchSlowRuns(days, mine, kind),
  });
  return (
    <>
      <div>
        <select
          aria-label="Run kind"
          className={control}
          value={kind}
          onChange={(e) => setKind(e.target.value)}
        >
          <option value="interactive">Interactive prompts</option>
          <option value="background">Background jobs / circuit steps</option>
        </select>
      </div>
      <p>
        Recorded jobs, longest finished elapsed time first. Elapsed includes queueing, execution,
        and any waiting for a reply. Open a prompt to inspect its conversation.
      </p>
      {isPending && <p>Loading runs…</p>}
      {error && <p role="alert">Could not load runs: {error.message}</p>}
      {data && (
        <div className="overflow-x-auto">
          <table className="w-full text-right text-xs">
            <thead className="border-cyber-border border-b">
              <tr>
                {[
                  "Prompt / source",
                  "Outcome",
                  "Elapsed",
                  "Queue / lock delay",
                  "Steps",
                  "Tools",
                ].map((label) => (
                  <th key={label} className="p-2 font-medium whitespace-nowrap first:text-left">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row) => (
                <tr key={row.jobId} className="border-cyber-border/30 border-b">
                  <td className="max-w-sm p-2 text-left">
                    <button
                      className="text-cyber-cyan line-clamp-2 cursor-pointer text-left hover:underline"
                      onClick={() => selectSession(row.threadKey)}
                    >
                      {row.prompt || row.jobId}
                    </button>
                    <div className="mt-1">
                      {row.source} · {row.origin} · {new Date(row.createdAt).toLocaleString()}
                    </div>
                    <div className="break-all">{row.model ?? "Model not recorded"}</div>
                  </td>
                  <td className="p-2">{row.status}</td>
                  <td className="p-2 whitespace-nowrap">{duration(row.elapsedMs)}</td>
                  <td className="p-2 whitespace-nowrap">{duration(row.queueMs)}</td>
                  <td className="p-2">{row.steps ?? "—"}</td>
                  <td className="p-2">{row.toolCalls ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.rows.length === 0 && (
            <p className="py-6 text-center">No accessible runs in this window.</p>
          )}
        </div>
      )}
      {data?.hasMore && (
        <p>Showing the 50 slowest accessible jobs. Narrow the date range for a smaller window.</p>
      )}
      <p>
        — means timing or usage is unavailable; active jobs have no final elapsed duration. Queue /
        lock delay uses the recorded lock timestamp and may include retry delays. Only persisted
        jobs are included, not unrecorded child work.
      </p>
    </>
  );
}

export function StatsPerformance({ days, mine }: { days: number; mine: boolean }) {
  const [view, setView] = useState("runs");
  return (
    <div className="text-cyber-muted space-y-4 text-xs">
      <div className="flex gap-2" role="tablist" aria-label="Performance views">
        {["runs", "capabilities"].map((value) => (
          <button
            key={value}
            role="tab"
            aria-selected={view === value}
            onClick={() => setView(value)}
            className={`${control} ${view === value ? "border-cyber-cyan text-cyber-cyan" : ""}`}
          >
            {value === "runs" ? "Slow runs" : "Capabilities"}
          </button>
        ))}
      </div>
      {view === "runs" ? (
        <Runs days={days} mine={mine} />
      ) : (
        <Capabilities days={days} mine={mine} />
      )}
    </div>
  );
}
